import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { WMSTileLayer, useMap } from 'react-leaflet';
import * as L from 'leaflet';
import { useTranslation } from 'react-i18next';
import { GlobalContext } from '../../App';
import { useCC } from '../../contexts/CCContext';
import { ccGeoserverOrigin, getCCViewport } from '../../services/CCGeoService';
import { classForCode, classLabel, featureStyle, investigationFeature, polygonAreaHa } from './contract';
import './cc.css';
import CCPublicationOverlay from './CCPublicationOverlay';
import { useYield } from './YieldContext';
import YieldOverlay from './YieldOverlay';
import { CC_MIN_TILE_ZOOM, CC_MAX_TILE_ZOOM, CC_MIN_VECTOR_ZOOM } from './mapZoom';

export default function CCOverlay() {
  const cc = useCC();
  const yieldView = useYield();
  if (yieldView?.view === 'yield') return yieldView.active ? <YieldOverlay /> : null;
  if (cc.transport === 'v2') return cc.active ? <CCPublicationOverlay /> : null;
  return <LegacyCCOverlay />;
}

function LegacyCCOverlay() {
  const map = useMap();
  const { dataset, metadata, setSelectedPolygon } = useCC();
  const { globalSelectedRegion } = useContext(GlobalContext);
  const { t, i18n } = useTranslation('translation');
  const [status, setStatus] = useState('idle');
  const [tileError, setTileError] = useState(false);
  const [revision, setRevision] = useState(0);
  const statusRef = useRef(null);
  const origin = ccGeoserverOrigin();
  const regionKey = globalSelectedRegion?.properties?.key;

  useEffect(() => {
    if (statusRef.current) {
      L.DomEvent.disableClickPropagation(statusRef.current);
      L.DomEvent.disableScrollPropagation(statusRef.current);
    }
  }, []);

  useLayoutEffect(() => {
    setTileError(false);
    if (!dataset || !metadata || !origin) { setStatus('idle'); return; }
    let generation = 0;
    let controller;
    let timer;
    let vectors;
    let alive = true;
    const pane = map.getPane('cc-polygons') || map.createPane('cc-polygons');
    pane.style.zIndex = '360'; // Keep saved fields above the CC overlay.
    const renderer = L.canvas({ padding: 0.2, pane: 'cc-polygons' });
    const clear = () => {
      generation += 1;
      controller?.abort();
      clearTimeout(timer);
      if (vectors) { vectors.eachLayer((layer) => layer.closeTooltip()); map.removeLayer(vectors); vectors = null; }
    };
    const load = async () => {
      clear();
      if (map.getZoom() < CC_MIN_VECTOR_ZOOM) { setStatus('idle'); return; }
      const requestId = generation;
      controller = new AbortController();
      const signal = controller.signal;
      const bounds = map.getBounds();
      const bbox = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()];
      setStatus('loading');
      try {
        const result = await getCCViewport({ dataset, bbox, signal });
        if (!alive || signal.aborted || requestId !== generation) return;
        vectors = L.geoJSON(result.features, {
          renderer,
          pane: 'cc-polygons',
          style: (feature) => featureStyle(metadata, feature),
          onEachFeature: (feature, layer) => {
            // Build tooltip lazily; 20,000 polygons must not create 20,000 DOM nodes.
            layer.on('mouseover', () => {
              layer.setStyle({ fillOpacity: Math.min(1, metadata.rendering.fill_opacity + 0.15) });
              const content = document.createElement('span');
              const p = feature.properties;
              const entry = classForCode(metadata, p.crop_code);
              const hectares = polygonAreaHa(feature);
              const diagnostic = entry === metadata.unmapped
                ? ` (${String(p.crop_code ?? 'null')}; ${p.class_schema}; ${String(p.source_crop_code ?? 'null')}; ${p.source_class_schema || '—'})` : '';
              content.textContent = `${classLabel(metadata, p.crop_code, t)}${diagnostic} · ${dataset.year}${hectares === null ? '' : ` · ${new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(hectares)} ha`}`;
              layer.bindTooltip(content, { sticky: true }).openTooltip();
            });
            layer.on('mouseout', () => { layer.setStyle(featureStyle(metadata, feature)); layer.closeTooltip(); });
            layer.on('click', () => setSelectedPolygon(investigationFeature(feature, dataset)));
          },
        }).addTo(map);
        setStatus(result.incomplete ? 'incomplete' : result.features.length ? 'ready' : 'emptyViewport');
      } catch (error) {
        if (!alive || signal.aborted || requestId !== generation) return;
        console.error('CC viewport request failed', error);
        setStatus('mapError');
      }
    };
    const onStart = () => { clear(); setStatus(map.getZoom() >= CC_MIN_VECTOR_ZOOM ? 'loading' : 'idle'); };
    const schedule = () => { clear(); timer = setTimeout(load, 200); };
    map.on('movestart zoomstart', onStart);
    map.on('moveend zoomend', schedule);
    load();
    return () => {
      alive = false;
      clear();
      map.off('movestart zoomstart', onStart);
      map.off('moveend zoomend', schedule);
      if (map.hasLayer(renderer)) map.removeLayer(renderer);
    };
  }, [map, dataset, metadata, origin, regionKey, revision, setSelectedPolygon, t, i18n.language]);

  return <>
    {dataset && origin && <WMSTileLayer key={`${dataset.id}:${revision}`} url={`${origin}/geoserver/gwc/service/wms`}
      layers={dataset.layer} styles="" version="1.1.1" format="image/png" transparent tiled
      minZoom={CC_MIN_TILE_ZOOM} maxZoom={CC_MAX_TILE_ZOOM} tileSize={256} zIndex={400}
      eventHandlers={{ tileerror: () => setTileError(true) }} />}
    <div ref={statusRef} className="cc-map-status" role="status">
      {dataset && <strong>{t('general.crop_intelligence')} · {dataset.year}</strong>}
      {dataset && (tileError || !['idle', 'ready'].includes(status)) && <div>
        {t(`cc.${tileError ? 'mapError' : status}`)}
        {(tileError || status === 'mapError') && <button onClick={() => setRevision((value) => value + 1)}>{t('cc.retry')}</button>}
      </div>}
    </div>
  </>;
}
